import { Observable } from "rxjs";
import { List, fromJS } from "immutable";
import diff from "immutablediff";
import siteConfig from "site-config";
import { ajax } from "lib/observable";
import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import { MutableType, mixedSetOperation, IOperation } from "actions/mutate";
import { getAndIncrementRequestId } from "./helpers";
import { parseReviewPlanValue, parseMetricReviewPlanValue } from "./helpers/review-plans";
import { getReviewPlanFromState } from "reducers/selectors/collection";
import { REVIEW_PLAN_ID_PREFIX } from "constants/product-collection";
import {
    getPinnedRows,
    PlanMetricRows,
} from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { getLegacyTypeByMetricsKind } from "helpers/metrics/legacy-metrics";
import lodashGet from "lodash/get";
import { MetricsKind } from "helpers/metrics/types";
interface IReviewPlanRequest {
    set_value: {
        dimension_value_ids: string[];
        plan_detail: {
            depth: string;
            retail: string;
            skus: number;
            units: number;
            wholesale: string;
        };
    };
}

export interface IReviewPlan {
    [key: string]: {
        struct_value: {
            depth: string;
            retail: string;
            skus: number;
            units: number;
            wholesale: string;
        };
    };
}

export const DIMENSION_IDS_DELIMITER = "#";

const normalizeReviewPlanOperation = (operation: IOperation): IReviewPlanRequest => {
    const { value, key } = operation;
    const planDetail = value.get("struct_value").toJS();
    return {
        set_value: {
            dimension_value_ids: key.split(DIMENSION_IDS_DELIMITER),
            plan_detail: planDetail,
        },
    };
};

const getDiffIndexesForRows = (operation: IOperation, planMetrics: string[]): { [key: string]: boolean } => {
    const diffIndexes = {};
    if (operation.deltas || operation.value) {
        Object.keys(PlanMetricRows).forEach((planIndex) => {
            diffIndexes[planIndex] = true;
        });
    }
    return diffIndexes;
};

const findAndSetMetricsValueInImmutableMap = (
    reviewPlanObj,
    metricKind: MetricsKind,
    reviewPlanValue: string | number
) => {
    if (!reviewPlanObj) {
        return null;
    }

    const jsReviewPlanObj = reviewPlanObj.toJS();
    const metricsArray = lodashGet(jsReviewPlanObj, "struct_value.metrics");
    if (metricsArray) {
        let foundMetric = false;
        for (const [i, metric] of metricsArray.entries()) {
            if (metric.kind === metricKind) {
                metric.value = { number_value: reviewPlanValue };
                foundMetric = true;
                break;
            }
        }

        if (!foundMetric) {
            metricsArray.push({ kind: metricKind, value: { number_value: reviewPlanValue } });
        }
    }

    let newObj;
    if (metricsArray) {
        // metrics array exists with the same metric
        newObj = reviewPlanObj.setIn(["struct_value", "metrics"], metricsArray);
    } else {
        // there's no metrics array we need to create one for the existing stored mutation
        newObj = reviewPlanObj.setIn(
            ["struct_value", "metrics"],
            [{ kind: metricKind, value: { number_value: reviewPlanValue } }]
        );
    }

    return newObj;
};

export const mutateReviewPlanEpic = (actions$, store) => {
    const buffer: any = {};
    return actions$
        .ofType(COLLECTIONS.COLLECTION.REVIEW_PLANS.MUTATE)
        .map(({ payload: { collectionId, dimensionPath, totalType, metricKind, value } }) => {
            const state = store.getState();
            const dimensionValueKey = dimensionPath.join(DIMENSION_IDS_DELIMITER);
            const { reviewPlan } = getReviewPlanFromState(state, collectionId);
            let reviewPlanValue;
            if (totalType) {
                reviewPlanValue = parseReviewPlanValue(value, totalType);
            } else if (metricKind) {
                reviewPlanValue = parseMetricReviewPlanValue(value, metricKind);
            }

            if (buffer[collectionId]) {
                const storedMutations = buffer[collectionId][dimensionValueKey];
                if (storedMutations) {
                    if (totalType) {
                        buffer[collectionId][dimensionValueKey] = storedMutations.setIn(
                            ["struct_value", totalType],
                            reviewPlanValue
                        );
                    } else if (metricKind) {
                        buffer[collectionId][dimensionValueKey] = findAndSetMetricsValueInImmutableMap(
                            storedMutations,
                            metricKind,
                            reviewPlanValue
                        );
                    }
                } else {
                    const reviewPlanDimension = reviewPlan.get(dimensionValueKey);
                    if (totalType) {
                        buffer[collectionId][dimensionValueKey] = reviewPlanDimension
                            ? reviewPlanDimension.setIn(["struct_value", totalType], reviewPlanValue)
                            : fromJS({
                                  struct_value: {
                                      [totalType]: reviewPlanValue,
                                  },
                              });
                    } else if (metricKind) {
                        buffer[collectionId][dimensionValueKey] = reviewPlanDimension
                            ? findAndSetMetricsValueInImmutableMap(reviewPlanDimension, metricKind, reviewPlanValue)
                            : fromJS({
                                  struct_value: {
                                      metrics: [{ kind: metricKind, value: { number_value: reviewPlanValue } }],
                                  },
                              });
                    }
                }
            } else {
                if (totalType) {
                    buffer[collectionId] = {
                        [dimensionValueKey]:
                            reviewPlan.get(dimensionValueKey)?.setIn(["struct_value", totalType], reviewPlanValue) ||
                            fromJS({
                                struct_value: {
                                    [totalType]: reviewPlanValue,
                                },
                            }),
                    };
                } else if (metricKind) {
                    buffer[collectionId] = {
                        [dimensionValueKey]:
                            findAndSetMetricsValueInImmutableMap(
                                reviewPlan.get(dimensionValueKey),
                                metricKind,
                                reviewPlanValue
                            ) ||
                            fromJS({
                                struct_value: {
                                    metrics: [{ kind: metricKind, value: { number_value: reviewPlanValue } }],
                                },
                            }),
                    };
                }
            }
            return buffer;
        })
        .debounce(() => Observable.interval(50)) // We borrow the heuristics of MUTATE_OPERATION
        .flatMap((buffer) => {
            const state = store.getState();
            const actions = Object.keys(buffer).reduce((actions, collectionId) => {
                const { reviewPlan, reviewPlanKey } = getReviewPlanFromState(state, collectionId);
                const newActions = Object.keys(buffer[collectionId]).map((dimensionKey) => {
                    const currentDeliveryPlan = reviewPlan.get(dimensionKey);
                    const newDimensionPlan = buffer[collectionId][dimensionKey];
                    return mixedSetOperation(
                        reviewPlanKey,
                        MutableType.Map,
                        "plan_detail",
                        dimensionKey,
                        newDimensionPlan,
                        currentDeliveryPlan && diff(currentDeliveryPlan, newDimensionPlan),
                        {
                            history: true,
                        }
                    );
                });
                actions.push(...newActions);
                return actions;
            }, []);
            buffer = {};
            return actions;
        });
};

export const syncReviewPlanEpic = (actions$, store) =>
    actions$
        .ofType(COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.START)
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();
            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload: { objectId } }: { payload: { objectId: string } }) => {
            const { collection, mutate, dimensionsPanel } = store.getState();
            const planMetrics = dimensionsPanel.applied.metrics.values.map((metric) =>
                getLegacyTypeByMetricsKind(metric.kind)
            );
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            let diffIndexes = {};
            uncommittedMutations.get(objectId).forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    diffIndexes = {
                        ...diffIndexes,
                        ...getDiffIndexesForRows(operation, planMetrics),
                    };
                    operations.push(normalizeReviewPlanOperation(operation));
                });
            });

            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const command = {
                operations,
                revision,
                req_id: requestId,
                allow_transformation: true,
            };

            store.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                },
            });

            const reviewPlanId = objectId.replace(REVIEW_PLAN_ID_PREFIX, "");

            return {
                command,
                sessionId,
                requestId,
                reviewPlanId,
                collectionId: committedHEAD.document_id,
                diffIndexes,
                merchantId,
            };
        })
        .concatMap(({ command, collectionId, requestId, sessionId, reviewPlanId, diffIndexes, merchantId }) => {
            const url = `/v1/collections/${collectionId}/review_plans/${reviewPlanId}/mutate?sid=${sessionId}&req_id=${requestId}`;
            return ajax({
                body: { ...command, merchant_id: merchantId },
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId, diffIndexes },
                    type: COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.COMPLETED,
                }))
                .catch((error) => {
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.COMPLETED,
                        })
                    );
                });
        });

export const loadReviewPlanRowsEpic = (actions$, store) =>
    actions$
        .ofType(COLLECTIONS.COLLECTION.FETCH.COMPLETED)
        .filter(({ error }) => !error)
        .concatMap(({ payload }) => {
            const {
                collection: { id },
            } = payload;
            const state = store.getState();
            const {
                routing: { locationBeforeTransitions },
                dimensionsPanel,
            } = state;
            const { poi, showTotals } = locationBeforeTransitions.query;
            const { reviewPlan } = getReviewPlanFromState(state, id, poi);
            const planMetrics = dimensionsPanel.applied.metrics.showAssortmentPlan
                ? dimensionsPanel.applied.metrics.values.map((metric) => getLegacyTypeByMetricsKind(metric.kind))
                : [];
            const rows = getPinnedRows(showTotals, planMetrics, true, reviewPlan.toJS());
            return [
                {
                    type: COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.LOAD,
                    payload: {
                        reviewPlanRows: rows,
                    },
                },
                {
                    type: COLLECTIONS.COLLECTION.REVIEW_PLANS.LOAD,
                    payload: {
                        reviewPlan: reviewPlan,
                    },
                },
            ];
        });

export const updateReviewPlanRowsEpic = (actions$, store) =>
    actions$.ofType(COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.COMPLETED).concatMap(({ payload }) => {
        const { collectionId, diffIndexes } = payload;
        const state = store.getState();
        const {
            routing: { locationBeforeTransitions },
        } = state;
        const { poi } = locationBeforeTransitions.query;
        const { reviewPlan } = getReviewPlanFromState(state, collectionId, poi);
        const mutations = [];

        if (diffIndexes) {
            mutations.push({
                type: COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.UPDATE,
                payload: {
                    indexes: Object.keys(diffIndexes),
                    reviewPlan: reviewPlan.toJS(),
                },
            });
        }

        if (reviewPlan) {
            mutations.push({
                type: COLLECTIONS.COLLECTION.REVIEW_PLANS.LOAD,
                payload: {
                    reviewPlan: reviewPlan,
                },
            });
        }

        return mutations;
    });

export const reloadReviewPlanRowsEpic = (actions$, store) =>
    actions$.ofType(COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.RELOAD).concatMap(({ payload }) => {
        const { collectionId, showTotals, dimensionPlanMetrics, accessPlan, poi } = payload;
        const state = store.getState();
        const { reviewPlan } = getReviewPlanFromState(state, collectionId, poi);
        const rows = getPinnedRows(showTotals, dimensionPlanMetrics, accessPlan, reviewPlan.toJS());
        return [
            {
                type: COLLECTIONS.COLLECTION.REVIEW_PLANS.ROWS.LOAD,
                payload: {
                    reviewPlanRows: rows,
                },
            },
            {
                type: COLLECTIONS.COLLECTION.REVIEW_PLANS.LOAD,
                payload: {
                    reviewPlan: reviewPlan,
                },
            },
        ];
    });
